import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isTintableSvg, preloadTintableSvg } from './tintableSvg';

const loadedAssets = new Set();
const inFlightAssets = new Map();
const linkedPreloadAssets = new Set();

function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function ensurePreloadLink(src) {
  if (!isBrowserEnvironment()) return;
  if (!src || linkedPreloadAssets.has(src)) return;

  const existing = document.head?.querySelector?.(`link[rel="preload"][href="${src}"]`);
  if (existing) {
    linkedPreloadAssets.add(src);
    return;
  }

  try {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
    linkedPreloadAssets.add(src);
    console.log(`[PRE-LOAD SYSTEM] Injected preload link for asset: ${src}`);
  } catch (error) {
    console.warn(`[PRE-LOAD SYSTEM] Failed to inject preload link for asset: ${src}`, error);
  }
}

export function preloadAsset(src, { priority = false } = {}) {
  if (!isBrowserEnvironment()) return Promise.resolve();
  if (!src || typeof src !== 'string') return Promise.resolve();

  if (loadedAssets.has(src)) {
    return Promise.resolve();
  }

  if (priority) {
    ensurePreloadLink(src);
  }

  if (isTintableSvg(src)) {
    preloadTintableSvg(src).catch(() => {});
  }

  if (inFlightAssets.has(src)) {
    return inFlightAssets.get(src);
  }

  const promise = new Promise((resolve) => {
    const image = new Image();
    if (priority && typeof image.fetchPriority === 'string') {
      image.fetchPriority = 'high';
    }
    image.decoding = 'async';
    image.loading = priority ? 'eager' : 'auto';

    let settled = false;
    const finalize = () => {
      if (settled) return;
      settled = true;
      loadedAssets.add(src);
      inFlightAssets.delete(src);
      console.log(`[PRE-LOAD SYSTEM] Asset primed: ${src}`);
      resolve();
    };

    const handleError = (event) => {
      if (settled) return;
      settled = true;
      inFlightAssets.delete(src);
      console.warn(`[PRE-LOAD SYSTEM] Failed to preload asset: ${src}`, event?.error || event);
      resolve();
    };

    const triggerDecode = () => {
      if (settled) return;
      if (typeof image.decode === 'function') {
        image
          .decode()
          .then(finalize)
          .catch((error) => {
            console.warn(`[PRE-LOAD SYSTEM] decode() failed for asset: ${src}`, error);
            finalize();
          });
      } else if (image.complete) {
        finalize();
      }
    };

    image.addEventListener('load', triggerDecode, { once: true });
    image.addEventListener('error', handleError, { once: true });

    console.log(`[PRE-LOAD SYSTEM] Starting asset preload: ${src}`);
    image.src = src;

    if (image.complete) {
      triggerDecode();
    }
  });

  inFlightAssets.set(src, promise);
  return promise;
}

export function useOnboardingAssetPreloader({
  manifest = {},
  priorityAssets = [],
  currentStep,
  windowAhead = 5,
} = {}) {
  const priorityList = useMemo(() => {
    const set = new Set();
    priorityAssets.forEach((asset) => {
      if (asset) set.add(asset);
    });
    return Array.from(set);
  }, [priorityAssets]);

  const stepKeys = useMemo(() => {
    return Object.keys(manifest)
      .map((key) => Number(key))
      .filter((key) => Number.isFinite(key))
      .sort((a, b) => a - b);
  }, [manifest]);

  const normalizeAssets = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (Array.isArray(value.assets)) return value.assets.filter(Boolean);
    return [];
  }, []);

  const ensureAssets = useCallback((label, assets, options = {}) => {
    const list = (assets || []).filter(Boolean);
    if (!list.length) return;
    console.log(`[PRE-LOAD SYSTEM] ${label} (${list.length} assets).`);
    list.forEach((src) => {
      preloadAsset(src, options);
      if (isTintableSvg(src)) {
        preloadTintableSvg(src).catch(() => {});
      }
    });
  }, []);

  const backgroundScheduledRef = useRef(false);

  useEffect(() => {
    if (!isBrowserEnvironment()) return;
    if (!priorityList.length) return;

    ensureAssets(`Priming ${priorityList.length} priority onboarding assets`, priorityList, { priority: true });
  }, [priorityList, ensureAssets]);

  useEffect(() => {
    if (!isBrowserEnvironment()) return;
    if (!Number.isInteger(currentStep)) return;

    const currentAssets = normalizeAssets(manifest[currentStep]);
    ensureAssets(`Ensuring assets for step ${currentStep}`, currentAssets, { priority: true });

    const aheadEntries = [];
    for (let offset = 1; offset <= windowAhead; offset += 1) {
      const targetStep = currentStep + offset;
      const assets = normalizeAssets(manifest[targetStep]);
      if (assets.length) {
        aheadEntries.push({ step: targetStep, assets });
      }
    }

    if (aheadEntries.length) {
      const stepRange = aheadEntries.map(({ step }) => step).join(', ');
      const totalAssets = aheadEntries.reduce((sum, entry) => sum + entry.assets.length, 0);
      console.log(`[PRE-LOAD SYSTEM] Queueing assets for steps ${stepRange} (${totalAssets} assets).`);

      aheadEntries.forEach((entry, index) => {
        let handled = false;
        const load = () => {
          if (handled) return;
          handled = true;
          ensureAssets(`Preloading assets for step ${entry.step}`, entry.assets);
        };

        const delay = 40 * (index + 1);
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(load, { timeout: 1000 + index * 250 });
          window.setTimeout(() => {
            if (!handled) {
              load();
            }
          }, delay);
        } else {
          window.setTimeout(load, delay);
        }
      });
    }

    if (!backgroundScheduledRef.current && stepKeys.length) {
      const remainingSteps = stepKeys.filter((step) => step > currentStep + windowAhead);
      const remainingAssets = remainingSteps.flatMap((step) => normalizeAssets(manifest[step]));
      if (remainingAssets.length) {
        let handled = false;
        const loadRemaining = () => {
          if (handled) return;
          handled = true;
          ensureAssets('Background preloading remaining assets', remainingAssets);
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(loadRemaining, { timeout: 4000 });
          window.setTimeout(loadRemaining, 400);
        } else {
          window.setTimeout(loadRemaining, 300);
        }
        console.log(`[PRE-LOAD SYSTEM] Scheduling background preloads (${remainingAssets.length} assets).`);
      }
      backgroundScheduledRef.current = true;
    }

    console.log(`[PRE-LOAD SYSTEM] Onboarding step ${currentStep} rendered. Cached assets: ${loadedAssets.size}.`);
  }, [currentStep, ensureAssets, manifest, normalizeAssets, stepKeys, windowAhead]);
}
